import { useEffect, useState } from 'react'
import type { JSX } from 'react'
import { Link } from 'react-router'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { useBrand } from '@/brand/context'
import { may, useActiveTenant } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { Field } from '@/ui/Field'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'
import { channelLabel } from '@/settings/follow-ups/channels'
import { summariseFollowUps } from '@/settings/follow-ups/summary'
import { useToast } from '@/ui/toast-context'
import { postAge } from './stages'

type ComposeData = Schema<'ComposeResponse'>
type Preview = Schema<'MessagePreviewResponse'>

/** `BRIEF_LENGTHS` and `BRIEF_CTAS`, in the words `outreach.html#compose` uses. */
const LENGTHS: readonly { value: string; label: string }[] = [
  { value: 'short', label: 'Short — under 90 words' },
  { value: 'medium', label: 'Medium — 90 to 150 words' },
]

const CTAS: readonly { value: string; label: string }[] = [
  { value: 'call', label: 'A 15-minute call' },
  { value: 'timeline', label: 'A reply with their timeline' },
  { value: 'profile', label: 'Permission to send a profile' },
]

/**
 * Mirrors `BriefRequest` in `api/app/v1/compose.py` (`MAX_OFFER`, `MAX_ICP`).
 * Bounded here as well as on the server because the 422 the server sends says
 * only "Request validation failed" with no field named, so a tenant who pastes a
 * 2,100-character offer is told to guess which control is wrong (WEB-14).
 *
 * The ladder and the daily cap left this screen in the settings consolidation's
 * second batch (2026-09-15): they are `/settings/follow-ups` now, and the card
 * below the brief says what they are and where to change them.
 */
const OFFER_MAX = 2000
const ICP_MAX = 500

/**
 * "What we say" — `design/screens/outreach.html#compose`, audit row §B4.
 *
 * Two things behind one page: the brief every message is generated from, and
 * the button that starts it. The ladder it goes out on, and how many a day, are
 * settings — `/settings/follow-ups` since 2026-09-15 — and are read here only.
 *
 * WHY THERE IS NO TEMPLATE BOX, WHICH IS THE WHOLE SCREEN
 * --------------------------------------------------------
 * The prototype states it: *"§5 asks for a message tailored to each prospect, so
 * there is no template box on this screen — what you write is the brief, and
 * every message is generated against the post that lead was found in. The
 * preview is a real prospect, not a placeholder with `{{first_name}}` in it,
 * because a merge-field preview hides exactly the failure you are checking for."*
 *
 * So *Preview* generates a real message for a real lead, using the same two model
 * calls a send makes, and spends the same money doing it. It is a button rather
 * than something the screen does on load for that reason.
 *
 * WHY SAVING AND STARTING ARE TWO PRESSES
 * -----------------------------------------
 * `PUT /outreach/brief` writes the brief and its `active` switch;
 * `POST /outreach/follow-ups/start` queues a first touch for everybody who has
 * never been written to. The prototype draws one button, and one button here
 * would be one press for two decisions — *"this is what we say"* and *"send it to
 * four hundred people"* — with no way back between them. The screen keeps the
 * prototype's single primary action and asks for confirmation in between, naming
 * the number.
 *
 * WHY A MEMBER SEES THIS READ-ONLY RATHER THAN NOT AT ALL
 * --------------------------------------------------------
 * `GET` is `inbox:read`, which every role holds, and every write is
 * `outreach:manage`, which stops at admin. A member who cannot see what their
 * organisation is sending cannot use the inbox they are being asked to answer.
 */
export function Compose(): JSX.Element {
  const { membership } = useActiveTenant()
  const manageable = may(membership, 'outreach:manage')
  const toast = useToast()

  const compose = useQuery(
    (signal) => call(() => api.GET('/api/v1/outreach/brief', { signal })),
    [],
  )

  if (compose.state.status === 'loading') return <Loading label="Loading what we say" lines={8} />
  if (compose.state.status === 'error')
    return (
      <div className="ar-stack ar-stack--loose">
        <h1 className="ar-h1">What we say</h1>
        <ErrorState failure={compose.state.failure} onRetry={compose.refetch} />
      </div>
    )

  return (
    <Editor
      data={compose.state.data}
      manageable={manageable}
      onSaved={() => {
        compose.refetch()
      }}
      toast={toast}
    />
  )
}

function Editor({
  data,
  manageable,
  onSaved,
  toast,
}: {
  data: ComposeData
  manageable: boolean
  onSaved: () => void
  toast: ReturnType<typeof useToast>
}): JSX.Element {
  const [offer, setOffer] = useState(data.brief.offer)
  const [offerMissing, setOfferMissing] = useState(false)
  const [icp, setIcp] = useState(data.brief.icp)
  const [length, setLength] = useState(data.brief.length)
  const [cta, setCta] = useState(data.brief.cta)
  const [quotePost, setQuotePost] = useState(data.brief.quote_post)
  const [confirming, setConfirming] = useState(false)
  // OUTR-97. The walk, and what has already been paid for.
  //
  // **Kept by position rather than as one preview**, because *Next →* and
  // *Previous* both cost two model calls if they regenerate, and walking back to
  // compare message 2 against message 3 is the entire reason the pager exists. A
  // press that has already been paid for is answered from here.
  const [previews, setPreviews] = useState<Record<number, Preview>>({})
  // Which of them is on screen. `0` before anything has been generated, which
  // is the empty state — no preview is ever at position 0.
  const [at, setAt] = useState(0)
  // The brief these were written against. A preview written before an edit is
  // still worth reading — it is what the tenant is comparing the edit *to* — so
  // it is marked rather than thrown away, and any regeneration replaces the lot.
  const [writtenFor, setWrittenFor] = useState('')

  // The server is the source of truth after every save, and a re-read has to
  // reach the form — otherwise a save that the server normalised (a trimmed
  // offer, a seeded ladder) leaves the screen showing what was typed rather than
  // what was stored.
  useEffect(() => {
    setOffer(data.brief.offer)
    setIcp(data.brief.icp)
    setLength(data.brief.length)
    setCta(data.brief.cta)
    setQuotePost(data.brief.quote_post)
  }, [data])

  const saveBrief = useMutation((active: boolean) =>
    call(() =>
      api.PUT('/api/v1/outreach/brief', {
        body: { offer, icp, length, cta, quote_post: quotePost, active },
      }),
    ),
  )
  const generate = useMutation((position: number) =>
    call(() => api.POST('/api/v1/outreach/brief/preview', { body: { step_no: 1, position } })),
  )
  const start = useMutation(() => call(() => api.POST('/api/v1/outreach/follow-ups/start', {})))

  const busy = saveBrief.pending || start.pending
  // The cap in force, as the server reports it — the field that edited it is on
  // `/settings/follow-ups` now, so there is no half-typed value to guard against.
  const capInForce = data.daily_cap

  // OUTR-97. What the previews on screen were written against. Any of these
  // changing is what makes them stale — the ladder and the cap do not, because
  // a preview is always the first touch and no rung's angle reaches it.
  const briefKey = JSON.stringify([offer, icp, length, cta, quotePost])
  const preview = previews[at] ?? null
  const stale = preview !== null && writtenFor !== briefKey

  /**
   * Show the preview at `position`, generating it only if it has to.
   *
   * **A cached preview is never regenerated by a navigation**, because every
   * generation is two live model calls against the tenant's budget and the
   * drawing's *"1 of 6"* invites exactly the back-and-forth that would pay for
   * the same message twice. `regenerate` is the one press that overrides it, and
   * it is a button of its own for that reason (OUTR-98).
   *
   * A regeneration after an edit drops the rest of the walk rather than leaving
   * one message from the new brief beside five from the old one, which would be
   * a comparison of nothing.
   */
  const show = (position: number, regenerate = false): void => {
    if (!regenerate && previews[position] !== undefined && writtenFor === briefKey) {
      setAt(position)
      return
    }
    void (async () => {
      const result = await generate.run(position)
      if (!result.ok) return
      const fresh = writtenFor === briefKey
      setPreviews((current) =>
        fresh ? { ...current, [position]: result.data } : { [position]: result.data },
      )
      setWrittenFor(briefKey)
      setAt(position)
    })()
  }

  const onSave = (active: boolean): void => {
    void (async () => {
      const brief = await saveBrief.run(active)
      if (!brief.ok) return
      toast.show({
        kind: 'success',
        title: 'Saved',
        body: active
          ? 'Sending is on. Nothing goes out until you start the first touches.'
          : 'Sending is off, so nothing will go out.',
      })
      onSaved()
    })()
  }

  const onStart = (): void => {
    void (async () => {
      const brief = await saveBrief.run(true)
      if (!brief.ok) {
        setConfirming(false)
        return
      }
      // The ladder is no longer saved here (WEB-7 guarded that write): it is
      // stored the moment it is saved under Settings, so what starts is what
      // the read-out below shows.
      const started = await start.run(undefined)
      setConfirming(false)
      if (started.ok) {
        toast.show({
          kind: 'success',
          title:
            started.data.queued === 0
              ? 'Everybody reachable is already in the ladder'
              : `Queued ${String(started.data.queued)} first touches`,
          body: `${String(started.data.pending)} leads are now mid-ladder. They go out inside your send window, up to ${String(capInForce)} a day.`,
        })
        onSaved()
      }
    })()
  }

  return (
    <div className="ar-stack ar-stack--loose">
      <div className="ar-row">
        <h1 className="ar-h1">What we say</h1>
        <span className="app-spacer" />
        {manageable ? (
          <div className="ar-btn-group">
            <Button
              size="sm"
              tone="ghost"
              busy={generate.pending}
              onClick={() => {
                show(1)
              }}
            >
              Preview a real message
            </Button>
            <Button
              size="sm"
              tone="secondary"
              busy={busy}
              onClick={() => {
                onSave(data.brief.active)
              }}
            >
              Save
            </Button>
            {/* Enabled as drawn (parity P-COMP-08); an empty brief is refused
                on the press, under the field. */}
            <Button
              size="sm"
              tone="primary"
              onClick={() => {
                if (offer.trim() === '') {
                  setOfferMissing(true)
                  return
                }
                setConfirming(true)
              }}
            >
              Save &amp; start sending
            </Button>
          </div>
        ) : null}
      </div>

      <InlineError failure={saveBrief.state.failure} />
      <InlineError failure={start.state.failure} />
      <InlineError failure={generate.state.failure} />

      <SendingState data={data} cap={capInForce} />

      <div className="app-lead">
        <div className="ar-stack">
          <div className="ar-card">
            <div className="ar-card__header">
              <h2>Your brief</h2>
              <span className="ar-card__spacer" />
              {/* OUTR-134. Body-sm, as `outreach.html:867` draws it —
                  `.app-lead__meta` is the caption size and belongs to the lead
                  detail this class was borrowed from. */}
              <span className="ar-subtle ar-text-body-sm">Used for every message</span>
            </div>
            <div className="ar-card__body ar-stack">
              {/* Three rows and no help line, as `outreach.html#compose` draws
                  it (parity P-COMP-02). */}
              <Field
                label="What you're offering"
                error={offerMissing ? 'Say what you are offering before sending.' : undefined}
              >
                {(props) => (
                  <textarea
                    {...props}
                    className="ar-textarea"
                    rows={3}
                    maxLength={OFFER_MAX}
                    disabled={!manageable}
                    value={offer}
                    onChange={(event) => {
                      setOffer(event.target.value)
                      setOfferMissing(false)
                    }}
                  />
                )}
              </Field>
              <Field
                label="Who it's for"
                help="Also scores each lead — the breakdown on the lead detail is measured against this sentence."
              >
                {(props) => (
                  <input
                    {...props}
                    className="ar-input"
                    type="text"
                    maxLength={ICP_MAX}
                    disabled={!manageable}
                    value={icp}
                    onChange={(event) => {
                      setIcp(event.target.value)
                    }}
                  />
                )}
              </Field>
              {/* Two columns beside the rail as well (parity P-COMP-03):
                  `ar-grid--2` folds below two 18rem tracks, and the brief card
                  is narrower than that next to the sidebar at 1280. */}
              <div className="ar-grid ar-grid--2 app-compose__pair">
                <Field label="Length">
                  {(props) => (
                    <select
                      {...props}
                      className="ar-select"
                      disabled={!manageable}
                      value={length}
                      onChange={(event) => {
                        setLength(event.target.value)
                      }}
                    >
                      {LENGTHS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  )}
                </Field>
                <Field label="Ask for">
                  {(props) => (
                    <select
                      {...props}
                      className="ar-select"
                      disabled={!manageable}
                      value={cta}
                      onChange={(event) => {
                        setCta(event.target.value)
                      }}
                    >
                      {CTAS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  )}
                </Field>
              </div>
              <label className="ar-switch">
                <input
                  type="checkbox"
                  disabled={!manageable}
                  checked={quotePost}
                  onChange={(event) => {
                    setQuotePost(event.target.checked)
                  }}
                />
                <span className="ar-switch__track" aria-hidden="true" />
                <span>
                  Quote the post back to them
                  <span className="ar-choice__desc">
                    Reply rates roughly double. It is also the only proof this was not a blast.
                  </span>
                </span>
              </label>
            </div>
          </div>

          <div className="ar-card" id="follow-ups">
            <div className="ar-card__header">
              <h2>Follow-ups</h2>
              <span className="ar-card__spacer" />
              <Link className="ar-btn ar-btn--ghost ar-btn--sm" to="/settings/follow-ups">
                Change in Settings →
              </Link>
            </div>
            <div className="ar-card__body ar-stack ar-stack--tight">
              <p style={{ margin: 0 }}>{summariseFollowUps(data.steps, data.daily_cap)}</p>
              <span className="ar-subtle ar-text-body-sm">
                The rungs a lead climbs after the first touch, and how many go out in a day. Any
                reply, on any channel, ends the ladder for that person.
              </span>
            </div>
          </div>
        </div>

        <div className="ar-stack">
          <PreviewCard preview={preview} stale={stale} busy={generate.pending} onShow={show} />
          <div className="ar-alert ar-alert--info">
            <span className="ar-alert__icon" aria-hidden="true">
              ◷
            </span>
            <div className="ar-alert__body">
              <strong>Every message carries an unsubscribe</strong>
              One click, no login, and the address is suppressed before the next send can start. It
              is added by the sender itself, so it cannot be left out of a campaign by accident.
            </div>
          </div>
        </div>
      </div>

      {confirming ? (
        <ConfirmStart
          ready={data.ready}
          pending={data.pending}
          cap={capInForce}
          busy={busy}
          onCancel={() => {
            setConfirming(false)
          }}
          onConfirm={onStart}
        />
      ) : null}
    </div>
  )
}

/** What is happening right now, above the form. The one thing a tenant opening
 *  this screen actually wants to know. */
function SendingState({ data, cap }: { data: ComposeData; cap: number }): JSX.Element {
  if (!data.brief.active)
    return (
      <div className="ar-alert ar-alert--info">
        <span className="ar-alert__icon" aria-hidden="true">
          ◷
        </span>
        <div className="ar-alert__body">
          <strong>Sending is off</strong>
          Nothing goes out to anybody while it is. Write the brief, check a preview against a real
          prospect, and press <em>Save &amp; start sending</em> when it reads the way you would
          write it yourself.
        </div>
      </div>
    )
  return (
    <div className="ar-alert ar-alert--success">
      <span className="ar-alert__icon" aria-hidden="true">
        ✓
      </span>
      <div className="ar-alert__body">
        <strong>Sending is on</strong>
        {data.sent_today} of {cap} follow-ups have gone out today, and {data.pending} leads are
        mid-ladder. Any reply, on any channel, stops the ladder for that person.
      </div>
    </div>
  )
}

/**
 * The preview card, and the walk through it — OUTR-97, 98, 99, 140…143.
 *
 * Empty until somebody presses the button, because generating one spends money
 * and a screen that spent money on load would be one.
 *
 * **The badge is the position in the walk rather than the channel**, which is
 * what the drawing puts there. The channel has not gone: it moved into the
 * attribution line, into the slot the drawing fills with *"batched"* — a word
 * that is wrong for this call, since `app/outreach/compose.py` states these two
 * prompts are deliberately **not** batched. One true fact in the place of a
 * false one, and the channel is the fact that matters: a WhatsApp message that
 * reads like an email is exactly the failure this card exists to catch.
 */
function PreviewCard({
  preview,
  stale,
  busy,
  onShow,
}: {
  preview: Preview | null
  stale: boolean
  busy: boolean
  onShow: (position: number, regenerate?: boolean) => void
}): JSX.Element {
  const brand = useBrand()
  if (preview === null)
    return (
      <div className="ar-card">
        <div className="ar-card__body">
          <Empty
            icon="○"
            title="No preview yet"
            body={`Press Preview a real message and ${brand.productName} writes one for a lead you have not written to yet, from their own post — then Next walks the rest. Each one costs a model call, which is why it is a button rather than something this page does on its own.`}
          />
        </div>
      </div>
    )

  const age = postAge(preview.posted_ago ?? null)
  const channel = channelLabel(preview.channel)

  return (
    <div className="ar-card">
      <div className="ar-card__header">
        <h2>Preview — {preview.lead_name ?? 'a real prospect'}</h2>
        <span className="ar-card__spacer" />
        <span className="ar-badge ar-badge--neutral">
          {preview.position} of {preview.total}
        </span>
      </div>
      <div className="ar-card__body ar-stack ar-stack--tight">
        {preview.post_text === null ? null : (
          <div className="ar-panel">
            <span className="ar-subtle app-lead__meta">
              {/* OUTR-141. The drawing dates the post — *"Her post, 12 Jul"* —
                  and OUTR-24 settled that a scrape states an age and never a
                  date. This is that decision on the second screen that shows a
                  post: the age it was when the search found it, or nothing. */}
              {age === null ? 'Their post' : `Their post · ${age} old when we found it`}
            </span>
            <p className="app-lead__post">{preview.post_text}</p>
          </div>
        )}
        {preview.subject === null ? null : (
          <div>
            <span className="ar-subtle app-lead__meta">Subject</span>
            <p className="app-compose__preview">{preview.subject}</p>
          </div>
        )}
        <p className="app-compose__msg">{preview.body}</p>
        <div className="ar-row app-compose__attrib">
          <span className="ar-subtle">
            {preview.tailored ? 'Haiku 4.5' : 'Standard wording'} · {channel}
          </span>
          <span className="ar-card__spacer" />
          {/* The money, where the drawing puts it. Absent rather than ₹0.00 when
              no model was reached: nothing was charged, and a zero in a mono
              column reads as a charge of nothing rather than as no charge. */}
          {preview.cost_display === null ? null : (
            <span className="ar-mono">{preview.cost_display}</span>
          )}
        </div>
        {preview.tailored ? null : (
          <span className="ar-subtle app-lead__meta">
            The model could not be reached, so this is the standard wording. A real send would read
            the same way until it can be.
          </span>
        )}
        {stale ? (
          <span className="ar-subtle app-lead__meta">
            Your brief has changed since this was written. Regenerate to read the current one.
          </span>
        ) : null}
      </div>
      <div className="ar-card__footer">
        <Button
          size="sm"
          tone="ghost"
          busy={busy}
          onClick={() => {
            onShow(preview.position, true)
          }}
        >
          Regenerate
        </Button>
        <span className="ar-card__spacer" />
        {preview.position > 1 ? (
          <Button
            size="sm"
            tone="quiet"
            disabled={busy}
            onClick={() => {
              onShow(preview.position - 1)
            }}
          >
            ← Previous
          </Button>
        ) : null}
        {preview.position < preview.total ? (
          <Button
            size="sm"
            tone="quiet"
            disabled={busy}
            onClick={() => {
              onShow(preview.position + 1)
            }}
          >
            Next →
          </Button>
        ) : null}
      </div>
    </div>
  )
}

/** The step between *"this is what we say"* and *"send it to four hundred
 *  people"*, which the prototype's single button does not have. */
function ConfirmStart({
  ready,
  pending,
  cap,
  busy,
  onCancel,
  onConfirm,
}: {
  ready: number
  pending: number
  cap: number
  busy: boolean
  onCancel: () => void
  onConfirm: () => void
}): JSX.Element {
  return (
    <div className="ar-card app-compose__confirm" role="group" aria-label="Start sending">
      <div className="ar-card__body ar-stack ar-stack--tight">
        <strong>
          {ready === 0
            ? 'Nothing new to queue'
            : `Queue a first touch for ${String(ready)} ${ready === 1 ? 'lead' : 'leads'}?`}
        </strong>
        <span className="ar-subtle app-lead__meta">
          {ready === 0
            ? `Every reachable lead is already in the ladder — ${String(pending)} of them. Starting again queues nothing.`
            : `They go out inside your send window, at most ${String(cap)} a day, and each one is written against that prospect's own post. ${String(pending)} leads are already mid-ladder.`}
        </span>
      </div>
      <div className="ar-card__footer">
        <Button onClick={onCancel}>Not yet</Button>
        <span className="ar-card__spacer" />
        <Button tone="primary" busy={busy} onClick={onConfirm}>
          Start sending
        </Button>
      </div>
    </div>
  )
}
